import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import type Hls from 'hls.js';
import { RotateCcw } from 'lucide-react';
import { coursesApi } from '@/api/endpoints';
import { PlayerControls, type NextLesson } from './PlayerControls';

interface Props {
  courseSlug: string;
  lessonSlug: string;
  title: string;
  autoPlay?: boolean;
  /** Video tugaganda "Keyingi dars" tugmasi */
  next?: NextLesson;
}

/** Link/kalit muddati o'tsa (403/410) yoki tarmoq uzilsa — ketma-ket shuncha marta yangi link olinadi */
const MAX_REFRESHES = 3;
const WATERMARK_MOVE_MS = 12_000;
const POSITION_SAVE_MS = 5_000;
const positionKey = (course: string, lesson: string) => `sqluz-video-pos:${course}/${lesson}`;

type ErrorKey = 'forbidden' | 'notFound' | 'unavailable' | 'network' | 'unsupported' | 'playError';

function readPosition(key: string): number {
  try {
    const v = Number(localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

function writePosition(key: string, sec: number | null) {
  try {
    if (sec == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(Math.floor(sec)));
  } catch {
    /* private rejim — pozitsiya saqlanmaydi */
  }
}

function playbackErrorKey(err: unknown): ErrorKey {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401 || status === 403) return 'forbidden';
    if (status === 404) return 'notFound';
    if (status === 503) return 'unavailable';
    if (!err.response) return 'network';
  }
  return 'playError';
}

/**
 * O'z serverimizdagi shifrlangan HLS dars videosi (sql/docs/VIDEO_PIPELINE.md).
 *
 * - Imzolangan playlist URL ni API dan oladi (axios — access token avtomatik yangilanadi)
 * - AES kalit so'rovi playlist ichidagi chipta bilan ketadi — header kerak emas, shuning uchun
 *   iOS Safari'ning native HLS'i ham ishlaydi
 * - Link muddati o'tsa yoki tarmoq uzilsa — joriy joyni saqlab yangi link bilan davom etadi
 * - Harakatlanuvchi watermark (kim tarqatganini ko'rsatadi); to'liq ekran konteyner bo'yicha,
 *   shuning uchun watermark ham ko'rinadi. Yuklab olish, PiP va o'ng tugma o'chirilgan.
 * - Qayerda to'xtaganini eslab qoladi (localStorage)
 * - Boshqaruv paneli o'zimizniki (PlayerControls): tezlik, ±10 s, klaviatura, keyingi dars
 *
 * hls.js faqat shu komponent ochilganda yuklanadi (alohida chunk; SSR ga kirmaydi).
 */
export function HlsPlayer({ courseSlug, lessonSlug, title, autoPlay = false, next }: Props) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // PlayerControls element o'zgarganda qayta ulanishi uchun state ham
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [resumedFrom, setResumedFrom] = useState<number | null>(null);
  const [watermark, setWatermark] = useState<string | null>(null);
  const [pos, setPos] = useState({ top: 8, left: 8 });
  const [error, setError] = useState<ErrorKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let hls: Hls | null = null;
    let refreshes = 0;
    let firstLoad = true;
    const storeKey = positionKey(courseSlug, lessonSlug);
    setError(null);
    setLoading(true);

    const fail = (key: ErrorKey) => {
      if (cancelled) return;
      hls?.destroy();
      hls = null;
      setLoading(false);
      setError(key);
    };

    // Bir vaqtda bitta yangilash: eski instance yangi link kelguncha xato chiqarishda davom etadi
    let refreshing = false;

    const retry = () => {
      if (cancelled || refreshing) return;
      if (refreshes >= MAX_REFRESHES) {
        fail('network');
        return;
      }
      refreshes += 1;
      refreshing = true;
      hls?.stopLoad();
      void start(video.currentTime, !video.paused);
    };

    const afterLoad = (seekTo: number, play: boolean, resumedAt: number) => {
      if (seekTo > 1 && video.duration && seekTo < video.duration - 5) video.currentTime = seekTo;
      setLoading(false);
      if (firstLoad) {
        firstLoad = false;
        if (resumedAt > 1) setResumedFrom(resumedAt);
        // Klaviatura (probel, strelkalar) darhol ishlashi uchun
        boxRef.current?.focus({ preventScroll: true });
      }
      if (play) video.play().catch(() => undefined);
    };

    async function start(resumeAt: number, play: boolean) {
      let url: string;
      try {
        const res = await coursesApi.playback(courseSlug, lessonSlug);
        if (cancelled) return;
        url = res.data.url;
        setWatermark(res.data.watermark ?? null);
      } catch (err) {
        refreshing = false;
        const key = playbackErrorKey(err);
        if (key === 'network' && refreshes < MAX_REFRESHES) {
          refreshes += 1;
          window.setTimeout(() => void start(resumeAt, play), 2000 * refreshes);
          return;
        }
        fail(key);
        return;
      }

      // light build: AES-128 bor; DRM/subtitr/alt-audio kerak emas (~2x kichik)
      const HlsImpl = (await import('hls.js/light')).default;
      if (cancelled || !video) return;
      refreshing = false;

      if (HlsImpl.isSupported()) {
        hls?.destroy();
        const instance = new HlsImpl({
          enableWorker: true,
          backBufferLength: 60,
          // Davom ettirish joyidan boshlab yuklaydi (0-segmentni behuda olmaydi)
          startPosition: resumeAt > 1 ? resumeAt : -1,
        });
        hls = instance;
        let mediaRecoveries = 0;
        instance.on(HlsImpl.Events.FRAG_LOADED, () => {
          refreshes = 0;
        });
        instance.on(HlsImpl.Events.ERROR, (_event, data) => {
          if (cancelled || hls !== instance) return;
          const status = data.response?.code;
          // 403/410 — link yoki kalit chiptasi eskirgan (uzoq pauza). hls.js 4xx ni fatal deb
          // hisoblamaydi va o'sha eskirgan URL ni qayta-qayta so'raydi — darhol yangi link olinadi.
          if (data.type === HlsImpl.ErrorTypes.NETWORK_ERROR && (status === 403 || status === 410)) {
            retry();
            return;
          }
          if (!data.fatal) return;
          if (data.type === HlsImpl.ErrorTypes.NETWORK_ERROR) {
            // Tarmoq uzilgan (hls.js o'z urinishlarini tugatgan) — yangi link bilan qayta
            retry();
          } else if (data.type === HlsImpl.ErrorTypes.MEDIA_ERROR && mediaRecoveries < 2) {
            mediaRecoveries += 1;
            if (mediaRecoveries === 2) instance.swapAudioCodec();
            instance.recoverMediaError();
          } else {
            fail('playError');
          }
        });
        instance.once(HlsImpl.Events.MANIFEST_PARSED, () => afterLoad(0, play, resumeAt));
        instance.loadSource(url);
        instance.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // iOS Safari (MSE siz): native HLS — kalit chiptasi URI ichida, header kerak emas
        video.src = url;
        video.addEventListener('loadedmetadata', () => afterLoad(resumeAt, play, resumeAt), { once: true });
      } else {
        fail('unsupported');
      }
    }

    const onNativeError = () => {
      if (!hls && video.error) retry();
    };
    video.addEventListener('error', onNativeError);

    const saveTimer = window.setInterval(() => {
      if (!video.paused && video.currentTime > 0) writePosition(storeKey, video.currentTime);
    }, POSITION_SAVE_MS);
    const onEnded = () => writePosition(storeKey, null);
    video.addEventListener('ended', onEnded);

    void start(readPosition(storeKey), autoPlay);

    return () => {
      cancelled = true;
      window.clearInterval(saveTimer);
      video.removeEventListener('error', onNativeError);
      video.removeEventListener('ended', onEnded);
      if (video.currentTime > 0 && !video.ended) writePosition(storeKey, video.currentTime);
      hls?.destroy();
      hls = null;
      video.removeAttribute('src');
      video.load();
    };
  }, [courseSlug, lessonSlug, autoPlay, attempt]);

  useEffect(() => {
    if (!watermark) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const move = () => setPos({ top: 5 + Math.random() * 75, left: 5 + Math.random() * 60 });
    move();
    const id = window.setInterval(move, reduce ? WATERMARK_MOVE_MS * 3 : WATERMARK_MOVE_MS);
    return () => window.clearInterval(id);
  }, [watermark]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === boxRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const box = boxRef.current;
    const video = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else if (box?.requestFullscreen) {
      void box.requestFullscreen().catch(() => undefined);
    } else {
      // iPhone: faqat tizim to'liq ekrani (watermark u yerda ko'rinmaydi)
      video?.webkitEnterFullscreen?.();
    }
  }, []);

  return (
    <div
      ref={(el) => {
        boxRef.current = el;
        setBoxEl(el);
      }}
      tabIndex={0}
      aria-label={title}
      onContextMenu={(e) => e.preventDefault()}
      className={`relative aspect-video w-full select-none overflow-hidden rounded-lg border border-line bg-black outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
        fullscreen ? 'rounded-none border-0' : ''
      }`}
    >
      <video
        ref={(el) => {
          videoRef.current = el;
          setVideoEl(el);
        }}
        className="absolute inset-0 h-full w-full"
        playsInline
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        aria-label={title}
      />

      {watermark && (
        <div
          aria-hidden
          className="pointer-events-none absolute z-10 select-none whitespace-nowrap text-[13px] font-semibold leading-tight text-white/40 [text-shadow:0_0_2px_rgba(0,0,0,0.7)]"
          style={{ top: `${pos.top}%`, left: `${pos.left}%`, transition: 'top 1.2s ease, left 1.2s ease' }}
        >
          {watermark}
        </div>
      )}

      <PlayerControls
        video={videoEl}
        box={boxEl}
        fullscreen={fullscreen}
        onToggleFullscreen={toggleFullscreen}
        disabled={loading || !!error}
        resumedFrom={resumedFrom}
        next={next}
      />

      {loading && !error && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="h-10 w-10 animate-spin rounded-full border-4 border-white/25 border-t-white" aria-label={t('common.loading')} />
        </div>
      )}

      {error && (
        <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/85 p-6 text-center text-white">
          <p className="max-w-md text-sm leading-relaxed">{t(`courses.player.${error}`)}</p>
          {error !== 'forbidden' && error !== 'unsupported' && (
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="btn btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"
            >
              <RotateCcw className="h-4 w-4" />
              {t('courses.player.retry')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
